import { Modal, Collapse, Typography, Button } from "antd";
import { CloseOutlined, EyeOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import React from "react";

const { Text } = Typography;
const { Panel } = Collapse;

const fieldLabels: Record<string, string> = {
    week: "Week",
    lastPeriod: "Last Period",
    periodLength: "Period Length",
    dueDate: "Estimated due date",
    estimationMethod: "Estimation Method",
    preWeight: "Mother's weight before pregnancy (kg)",
    currentWeight: "Mother's weight (kg)",
    height: "Mother's height (cm)",
    bmi: "BMI",
    bp: "Arterial Pressure",
    pulse: "Pulse rate (min)",
    fmf: "FMF - First trimester screening",
    vitae: "Vitae",
    family: "Family",
    gyn: "Gyn",
    method: "Method",
    conditions: "Conditions",
    pregnancy: "Pregnancy",
    fetalPosition: "Fetal Position",
    placentalLocation: "Placental Location",
    umbilicalCord: "Umbilical Cord",
    amountOfAmnioticFluid: "Amount of amniotic fluid",
    bpd: "Biparietal diameter (BPD)",
    hc: "Head circumference (HC)",
    ac: "Abdominal circumference (AC)",
    fl: "Femur (FL)",
    diagnosis: "Diagnosis",
    recommendations: "Recommendations",
};

interface FullscreenPreviewProps {
    open: boolean;
    isEdit: boolean;
    onClose: () => void;
    previewData: Record<string, any>;
}

const formatValue = (field: string, value: any): React.ReactNode => {
    if (value === undefined || value === null || value === "") {
        return <Text type="secondary">— Not filled —</Text>;
    }
    if (Array.isArray(value)) {
        return value.length ? value.join(", ") : <Text type="secondary">— Not filled —</Text>;
    }
    if ((field === "lastPeriod" || field === "dueDate") && dayjs(value).isValid()) {
        return dayjs(value).format("DD.MM.YYYY");
    }
    return String(value);
};

const ObstetricExaminationPreviewModal: React.FC<FullscreenPreviewProps> = ({
    open,
    isEdit,
    onClose,
    previewData,
}) => {
    if (!open) return null;

    const sections: [string, string[]][] = [
        ["Pregnancy", ["week", "lastPeriod", "periodLength", "dueDate", "estimationMethod"]],
        ["Medical Data", ["preWeight", "currentWeight", "height", "bmi", "bp", "pulse", "fmf"]],
        ["Medical History", ["vitae", "family", "gyn"]],
        ["Examination", ["method", "conditions", "pregnancy"]],
        ["Measurements", ["fetalPosition", "placentalLocation", "umbilicalCord", "amountOfAmnioticFluid", "bpd", "hc", "ac", "fl"]],
        ["Diagnosis", ["diagnosis"]],
        ["Recommendations", ["recommendations"]],
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            destroyOnClose
            centered
            zIndex={7000}
            getContainer={() => document.body}
            maskStyle={{ zIndex: 6999 }}
            // Large full-screen modal
            width="85vw"
            styles={{
                content: {
                    height: "90vh",
                    display: "flex",
                    flexDirection: "column",
                    borderRadius: 12,
                    overflow: "hidden",
                },
                header: {
                    borderBottom: "1px solid #f0f0f0",
                    padding: "4px 20px 4px 20px",
                    textAlign: "center",
                    marginTop: -4,
                },
                body: {
                    flex: 1,
                    overflowY: "auto",
                    padding: "16px 24px",
                    background: "#fff",
                },
                footer: {
                    borderTop: "1px solid #f0f0f0",
                    padding: "12px 24px",
                    textAlign: "right",
                },
            }}
            closeIcon={<CloseOutlined style={{ color: "#999", fontSize: 18 }} />}
            title={
                <div style={{ textAlign: "center" }}>
                    <EyeOutlined style={{ color: "#1677ff", marginRight: 8, fontSize: 24 }} />
                    <span style={{ fontWeight: 600, fontSize: "1.5rem", color: "#1f1f1f" }}>
                        Obstetric Examination Preview
                    </span>
                    <div
                        style={{
                            fontSize: 14,
                            fontWeight: 400,
                            color: "#6b7280",
                            marginTop: 8,
                        }}
                    >
                        Showing detailed information for this patient’s obstetric examination
                    </div>
                </div>
            }
            footer={
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        flexWrap: "wrap",
                        gap: 12,
                        width: "100%",
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            textAlign: "center",
                            gap: 6,
                            background: "#fffbe6",
                            border: "1px solid #ffe58f",
                            borderRadius: 6,
                            padding: "6px 10px",
                            minHeight: 36,
                        }}
                    >
                        <span style={{ fontSize: 15 }}>⚠️</span>
                        <span style={{ fontSize: 13, lineHeight: 1.4 }}>
                            <strong>This is a preview.</strong> Any edits you make are{" "}
                            <strong>not yet saved</strong>. Please click{" "}
                            <strong>{isEdit ? "Update" : "Save"}</strong> after reviewing to apply them permanently.
                        </span>
                    </div>
                </div>
            }
            rootClassName="obstetric-preview-modal"
        >
            <Collapse
                defaultActiveKey={sections.map(([s]) => s)}
                accordion={false}
                bordered={false}
                expandIconPosition="end"
            >
                {sections.map(([section, fields]) => (
                    <Panel
                        key={section}
                        header={
                            <div
                                style={{
                                    background: "linear-gradient(90deg, #1677ff, #4096ff)",
                                    padding: "10px 14px",
                                    borderRadius: 6,
                                    fontWeight: 600,
                                    color: "#fff",
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                                }}
                            >
                                {section}
                            </div>
                        }
                        style={{
                            background: "transparent",
                            border: "1px solid #f0f0f0",
                            borderRadius: 10,
                            marginBottom: 16,
                            overflow: "hidden",
                        }}
                    >
                        <div style={{ display: "table", width: "100%", borderSpacing: 0 }}>
                            {fields.map((field, index) => (
                                <div
                                    key={field}
                                    style={{
                                        display: "table-row",
                                        backgroundColor: index % 2 === 0 ? "#ffffff" : "#fafafa",
                                        transition: "background 0.2s",
                                    }}
                                    onMouseEnter={(e) =>
                                        (e.currentTarget.style.backgroundColor = "#f0f5ff")
                                    }
                                    onMouseLeave={(e) =>
                                    (e.currentTarget.style.backgroundColor =
                                        index % 2 === 0 ? "#ffffff" : "#fafafa")
                                    }
                                >
                                    <div
                                        style={{
                                            display: "table-cell",
                                            width: "40%",
                                            fontWeight: 500,
                                            padding: "8px 12px",
                                            verticalAlign: "top",
                                            color: "#333",
                                            borderBottom: "1px solid #f5f5f5",
                                        }}
                                    >
                                        {fieldLabels[field] || field}
                                    </div>
                                    <div
                                        style={{
                                            display: "table-cell",
                                            width: "60%",
                                            padding: "8px 12px",
                                            verticalAlign: "top",
                                            borderBottom: "1px solid #f5f5f5",
                                        }}
                                    >
                                        {formatValue(field, previewData[field])}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Panel>
                ))}
            </Collapse>
        </Modal>
    );
};

export default ObstetricExaminationPreviewModal;
